import { Business, Email, LocationOn, Phone } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { submitContactForm } from "../../API/API";
import SEO from "../../components/SEO";

const ContactUs = () => {
  // Form State
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobileNumber: "",
    subjectType: "",
    discription: "",
  });

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Error State
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Handle Input Change
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  // Validate Form Fields
  const validateForm = () => {
    let newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Name is required";

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.mobileNumber.replace("-", ""))) {
      newErrors.mobileNumber = "Phone number must be 10 digits";
    }

    if (!formData.subjectType.trim())
      newErrors.subjectType = "Subject is required";

    if (!formData.discription.trim())
      newErrors.discription = "Message is required";

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0; // Return true if no errors
  };

  // Handle Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      setLoading(true);
      try {
        const response = await submitContactForm(formData);
        setAlert({
          open: true,
          message: "Your message has been sent successfully!",
          severity: "success",
        });
        // Clear form
        setFormData({
          name: "",
          email: "",
          mobileNumber: "",
          subjectType: "",
          discription: "",
        });
        setErrors({}); // Reset errors
      } catch (error) {
        console.error("Error submitting form:", error);
        setAlert({
          open: true,
          message: "Failed to send message. Please try again later.",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <SEO
        title="Contact Dequitec – Fast MSME Loan Assistance"
        description="Reach out to Dequitec for MSME or unsecured business loan guidance, fast approval advice, and expert support to fund your business quickly."
        canonical="https://www.dequitec.com/contact-us"
      />
      <Box sx={{ py: 4, px: 2 }}>
        {/* Header Section */}
        <Grid
          container
          spacing={6}
          alignItems="center"
          justifyContent={"center"}
        >
          <Grid item xs={12} md={6} lg={6}>
            <Paper
              elevation={0}
              sx={{ borderRadius: 2, padding: { lg: 4, xs: 0, md: 4, sm: 4 } }}
            >
              <img
                src="/contactUs.png"
                alt="Careers"
                style={{
                  height: "60vh",
                  borderRadius: "12px",
                  objectFit: "contain",
                  width: "100%",
                }}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            {/* Centered Line & Title */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                marginBottom: "1.5rem",
              }}
            >
              <Box
                sx={{
                  width: "40px",
                  height: "1px",
                  backgroundColor: "#004AAD",
                }}
              />
              <Typography
                variant="h7"
                sx={{ fontWeight: "bold", color: "#004AAD" }}
              >
                Contact Us
              </Typography>
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                marginBottom: "0.8rem",
                fontSize: "1.75rem",
              }}
            >
              Get in Touch with Us!
            </Typography>
            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: 400,
                lineHeight: 1.5,
                color: "#555",
              }}
            >
              Have questions or need assistance? We're here to help! Reach out
              to us for any queries related to business funding, partnerships,
              or support.
            </Typography>
          </Grid>
        </Grid>

        {/* Centered Line & Title for Query */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            marginTop: { lg: "0%", xs: "4%", md: "1%", sm: "4%" },
          }}
        >
          <Box
            sx={{ width: "40px", height: "2px", backgroundColor: "#004AAD" }}
          />
          <Typography
            variant="h8"
            sx={{ fontWeight: "bold", color: "#004AAD" }}
          >
            Query
          </Typography>
        </Box>
        <Box textAlign="center" marginTop={1}>
          {/* Subtitle */}
          <Typography
            variant="h6"
            sx={{
              mt: 1,
              mb: 2,
              fontWeight: "bold",
              color: "#333333",
              textAlign: "center",
            }}
          >
            Send Us Your Query
          </Typography>
        </Box>

        <Grid container justifyContent="center" mt={4}>
          <Grid item xs={12} sm={8}>
            <Card
              sx={{
                backgroundColor: "#ECF4DE",
                borderRadius: 2,
                p: { lg: 4, md: 4, sm: 2, xs: 2 },
                px: { lg: 15, md: 10, sm: 2, xs: 2 },
              }}
            >
              <CardContent>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    {/* Name Field */}
                    <Grid item xs={12}>
                      <Typography fontSize={12} marginBottom={1}>
                        Enter your full name so we can address you properly.
                      </Typography>
                      <TextField
                        fullWidth
                        label={
                          <span>
                            Full Name<span style={{ color: "red" }}>*</span>
                          </span>
                        }
                        name="name"
                        variant="outlined"
                        value={formData.name}
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        slotProps={{
                          inputLabel: {
                            shrink: true, // Ensures the label moves up properly
                          },
                        }}
                        placeholder="Enter your Name"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                          },
                        }}
                      />
                    </Grid>

                    {/* Email Field */}
                    <Grid item xs={12}>
                      <Typography fontSize={12} marginBottom={1}>
                        Provide your email address for us to get back to you.
                      </Typography>
                      <TextField
                        fullWidth
                        label={
                          <span>
                            Email Address<span style={{ color: "red" }}>*</span>
                          </span>
                        }
                        name="email"
                        variant="outlined"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        slotProps={{
                          inputLabel: {
                            shrink: true, // Ensures the label moves up properly
                          },
                        }}
                        placeholder="Enter your Email"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                          },
                        }}
                      />
                    </Grid>

                    {/* Phone Number Field */}
                    <Grid item xs={12}>
                      <Typography fontSize={12} marginBottom={1}>
                        Share your contact number for quicker assistance.
                      </Typography>
                      <TextField
                        fullWidth
                        label={
                          <span>
                            Phone Number<span style={{ color: "red" }}>*</span>
                          </span>
                        }
                        name="mobileNumber"
                        variant="outlined"
                        type="num"
                        value={formData.mobileNumber}
                        inputProps={{ maxLength: 10 }}
                        onChange={(e) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            ""
                          );
                          // e.target.value = e.target.value.replace(
                          //   /(\d{3})(\d{3})(\d{4})/,
                          //   "$1-$2-$3"
                          // );
                          handleChange(e);
                        }}
                        error={!!errors.mobileNumber}
                        helperText={errors.mobileNumber}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                          },
                        }}
                        slotProps={{
                          inputLabel: {
                            shrink: true, // Ensures the label moves up properly
                          },
                        }}
                        placeholder="+91 XXXXX XXXX XX"
                      />
                    </Grid>

                    {/* Subject Dropdown */}
                    <Grid item xs={12}>
                      <Typography fontSize={12} marginBottom={1}>
                        Select the purpose of your inquiry – General Inquiry,
                        Loan Assistance, Partnership, or Others.
                      </Typography>
                      <TextField
                        fullWidth
                        label={
                          <span>
                            Choose Subject
                            <span style={{ color: "red" }}>*</span>
                          </span>
                        }
                        name="subjectType"
                        variant="outlined"
                        select
                        SelectProps={{ native: true }}
                        value={formData.subjectType}
                        onChange={handleChange}
                        error={!!errors.subjectType}
                        helperText={errors.subjectType}
                        slotProps={{
                          inputLabel: {
                            shrink: true, // Ensures the label moves up properly
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                          },
                        }}
                        placeholder="e.g. General Inquiry"
                      >
                        <option value="">Select a subject</option>
                        <option value="business">Business Inquiry</option>
                        <option value="support">Technical Support</option>
                        <option value="partnerships">Partnerships</option>
                        <option value="help">Help</option>
                      </TextField>
                    </Grid>

                    {/* Message Field */}
                    <Grid item xs={12}>
                      <Typography fontSize={12} marginBottom={1}>
                        Describe your query in detail, and our team will respond
                        as soon as possible.
                      </Typography>
                      <TextField
                        fullWidth
                        label="Message"
                        name="discription"
                        variant="outlined"
                        multiline
                        rows={4}
                        value={formData.discription}
                        onChange={handleChange}
                        error={!!errors.discription}
                        helperText={errors.discription}
                        slotProps={{
                          inputLabel: {
                            shrink: true, // Ensures the label moves up properly
                          },
                        }}
                        placeholder="Enter your Message"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "white",
                          },
                        }}
                      />
                    </Grid>

                    {/* Submit Button */}
                    <Grid item xs={12} mt={2} textAlign="center">
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                          backgroundColor: "#004AAD",
                          textTransform: "none",
                          width: "70%",
                        }}
                      >
                        {loading ? "Sending..." : "Send Message"}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Centered Line & Title for Reach Out */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            marginTop: "2rem",
          }}
        >
          <Box
            sx={{ width: "40px", height: "2px", backgroundColor: "#004AAD" }}
          />
          <Typography
            variant="h8"
            sx={{ fontWeight: "bold", color: "#004AAD" }}
          >
            Reach Out to Us
          </Typography>
        </Box>
        <Box textAlign="center" marginTop={1}>
          {/* Subtitle */}
          <Typography
            variant="h6"
            sx={{
              mt: 1,
              mb: 2,
              fontWeight: "bold",
              color: "#333333",
              textAlign: "center",
            }}
          >
            Contact us via phone, email, or visit our office.
          </Typography>
        </Box>

        {/* Section 2: Contact Details & Address */}
        <Grid
          container
          spacing={5}
          justifyContent="center"
          gap={1}
          sx={{ marginTop: 4 }}
          px={{ lg: 14, md: 4, sm: 2, xs: 1 }}
        >
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={4}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Card
              sx={{
                borderRadius: 2,
                padding: 2,
                paddingLeft: 2.5,
                backgroundColor: "#F4EFFF",
                height: 200,
                width: 600,
              }}
            >
              <CardContent
                sx={{ padding: 0, "&:last-child": { paddingBottom: 0 } }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Phone sx={{ marginRight: 1 }} fontSize="small" />
                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    Contact Details
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.775rem",
                    marginTop: 1.5,
                    color: "#666",
                    fontFamily: "'Inter', sans-serif",
                    marginBottom: 3,
                  }}
                >
                  Call or email us for any assistance.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Phone
                      sx={{ marginRight: 1.5, color: "#333" }}
                      fontSize="small"
                    />
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 500,
                        fontFamily: "'Inter', sans-serif",
                        color: "#333",
                      }}
                    >
                      +91 9220 5899 29
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Email
                      sx={{ marginRight: 1.5, color: "#333" }}
                      fontSize="small"
                    />
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 500,
                        fontFamily: "'Inter', sans-serif",
                        color: "#333",
                      }}
                    >
                      contact@dequitec.com
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={4}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <Card
              sx={{
                borderRadius: 2,
                padding: 2,
                paddingLeft: 2.5,
                backgroundColor: "#F4EFFF",
                height: 200,
                width: 600,
              }}
            >
              <CardContent
                sx={{ padding: 0, "&:last-child": { paddingBottom: 0 } }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Business sx={{ marginRight: 1 }} fontSize="small" />
                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      fontFamily: "'Inter', sans-serif",
                    }}
                  >
                    Our Address
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.775rem",
                    marginTop: 1.5,
                    color: "#666",
                    fontFamily: "'Inter', sans-serif",
                    marginBottom: 3,
                  }}
                >
                  Visit us at our office location.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginTop: 0,
                  }}
                >
                  <LocationOn
                    sx={{ marginRight: 1.5, color: "#333", marginTop: "2px" }}
                    fontSize="small"
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 500,
                        fontFamily: "'Inter', sans-serif",
                        color: "#333",
                        lineHeight: 1.4,
                      }}
                    >
                      Smyan Tech Solutions Pvt Ltd
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 500,
                        fontFamily: "'Inter', sans-serif",
                        color: "#333",
                        lineHeight: 1.4,
                      }}
                    >
                      Plot no. 145, Sector 44,
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 500,
                        fontFamily: "'Inter', sans-serif",
                        color: "#333",
                        lineHeight: 1.4,
                      }}
                    >
                      Gurgaon – 122003
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Section 3: Social Media */}
        <Box textAlign="center" marginTop={4}>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#333", fontStyle: "italic" }}
          >
            Follow us on social media for updates and insights.
          </Typography>
        </Box>
        <Grid container justifyContent="center" sx={{ marginTop: 3 }}>
          <Grid item>
            <Box sx={{ display: "flex", gap: 3, alignItems: "center" }}>
              <IconButton
                onClick={() => window.open("https://facebook.com", "_blank")}
              >
                <img
                  src="/facebook.jpg"
                  alt="Facebook"
                  style={{ width: 40, height: 40 }}
                />
              </IconButton>
              <IconButton
                onClick={() => window.open("https://instagram.com", "_blank")}
              >
                <img
                  src="/instagram.png"
                  alt="Instagram"
                  style={{ width: 40, height: 40 }}
                />
              </IconButton>
              <IconButton
                onClick={() =>
                  window.open(
                    "https://www.linkedin.com/company/dequitec",
                    "_blank"
                  )
                }
              >
                <img
                  src="/linkedin.png"
                  alt="LinkedIn"
                  style={{ width: 40, height: 40 }}
                />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={handleCloseAlert} severity={alert.severity}>
            {alert.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default ContactUs;
